import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import {
  CVSS_ATTACK_COMPLEXITY,
  CVSS_ATTACK_VECTOR,
  CVSS_PRIVILEGES_REQUIRED,
  IssueFilterKeys,
} from '../../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../../redux/savedFilters/hooks';
import { FilterPanel } from '../../../generic/Filter';
import FilterCardCheckboxes from '../../../generic/Filter/FilterCardCheckboxes';
import useFetchIssuesAction from '../../IssuesInbox/hooks/useFetchIssuesAction';
import type { VulnFilter } from '../../../../../shared/issuesV2';
import type { SelectOption } from '../../../../types';

export type CvssMetricType = 'attackVector' | 'attackComplexity' | 'privilegesRequired';

type MetricConfig = {
  filterKey: IssueFilterKeys;
  label: string;
  testId: string;
  options: SelectOption[];
};

/**
 * Converts a CVSS filter value from `shared/issuesV2/common.ts` (e.g. 'network')
 * into a `SelectOption` with a title-cased label (e.g. 'Network'). Relies on the
 * fact that every descriptive CVSS filter value is a single lowercase word.
 */
const toOption = (value: string): SelectOption => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
});

const METRIC_CONFIG: Record<CvssMetricType, MetricConfig> = {
  attackVector: {
    filterKey: IssueFilterKeys.CvssAttackVector,
    label: 'Attack Vector',
    testId: 'issues-vuln-cvss-attack-vector',
    options: CVSS_ATTACK_VECTOR.map(toOption),
  },
  attackComplexity: {
    filterKey: IssueFilterKeys.CvssAttackComplexity,
    label: 'Attack Complexity',
    testId: 'issues-vuln-cvss-attack-complexity',
    options: CVSS_ATTACK_COMPLEXITY.map(toOption),
  },
  privilegesRequired: {
    filterKey: IssueFilterKeys.CvssPrivilegesRequired,
    label: 'Privileges Required',
    testId: 'issues-vuln-cvss-privileges-required',
    options: CVSS_PRIVILEGES_REQUIRED.map(toOption),
  },
};

type Props = {
  metric: CvssMetricType;
};

export const CvssMetricFilter: VoidFunctionComponent<Props> = ({ metric }) => {
  const { filterKey, label, testId, options } = METRIC_CONFIG[metric];
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilterHasChanged = useSetFilterHasChanged();

  const selected = (stateFilters as VulnFilter)[filterKey as keyof VulnFilter] as string[] | undefined;
  const selectedCheckboxes = (selected ?? []).map((value) => ({
    label: options.find((option) => option.value === value)?.label ?? value,
    value,
  }));

  const onCheckboxChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [filterKey]: newSelected.map(({ value }) => value),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardCheckboxes
        label={label}
        options={options}
        values={selectedCheckboxes}
        onChange={onCheckboxChange}
        testId={testId}
      />
    </FilterPanel>
  );
};
